/**
 * @fileoverview Number Pad Component
 * @description Input pad for entering numbers 1-9 and controls
 */

import React, { memo } from "react";
import { View, Text, Pressable, useColorScheme } from "react-native";
import { Ionicons } from "@expo/vector-icons";

interface NumberPadProps {
  /** Called when a number (1-9) is pressed */
  onNumberPress: (num: number) => void;
  /** Called when clear/erase is pressed */
  onClearPress: () => void;
  /** Called when undo is pressed */
  onUndoPress: () => void;
  /** Called when hint is pressed */
  onHintPress: () => void;
  /** Called when notes mode is toggled */
  onNotesToggle: () => void;
  /** Whether notes mode is active */
  isNotesMode: boolean;
  /** Number of hints remaining */
  hintsRemaining: number;
  /** Numbers that are "complete" (placed 9 times) - only disables in normal mode */
  completedNumbers?: number[];
  /** Whether the pad is disabled */
  disabled?: boolean;
}

/**
 * Number input pad with control buttons
 */
export const NumberPad = memo<NumberPadProps>(
  ({
    onNumberPress,
    onClearPress,
    onUndoPress,
    onHintPress,
    onNotesToggle,
    isNotesMode,
    hintsRemaining,
    completedNumbers = [],
    disabled = false,
  }) => {
    const colorScheme = useColorScheme();
    const isDark = colorScheme === "dark";

    const buttonSize = 56;
    const gap = 8;

    const NumberButton = ({
      num,
      isCompleted,
    }: {
      num: number;
      isCompleted: boolean;
    }) => {
      // In notes mode, completed numbers should still be pressable
      const isDisabled = disabled || (isCompleted && !isNotesMode);

      return (
      <Pressable
        onPress={() => onNumberPress(num)}
        disabled={isDisabled}
        accessibilityRole="button"
        accessibilityLabel={`Nummer ${num}`}
        accessibilityState={{ disabled: isDisabled }}
        style={({ pressed }) => ({
          width: buttonSize,
          height: buttonSize,
          borderRadius: 12,
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: isDisabled
            ? isDark
              ? "#1F2937"
              : "#E5E7EB"
            : pressed
            ? isDark
              ? "#374151"
              : "#D1D5DB"
            : isDark
            ? "#1F2937"
            : "#F3F4F6",
          opacity: isDisabled ? 0.4 : 1,
        })}
      >
        <Text
          style={{
            fontSize: 24,
            fontWeight: "600",
            color: isDisabled
              ? isDark
                ? "#6B7280"
                : "#9CA3AF"
              : isDark
              ? "#F9FAFB"
              : "#111827",
          }}
        >
          {num}
        </Text>
      </Pressable>
    );
    };

    const ControlButton = ({
      icon,
      label,
      onPress,
      isActive,
      badge,
    }: {
      icon: keyof typeof Ionicons.glyphMap;
      label: string;
      onPress: () => void;
      isActive?: boolean;
      badge?: number;
    }) => (
      <Pressable
        onPress={onPress}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityState={{ disabled, selected: isActive }}
        style={[
          {
            width: buttonSize,
            height: buttonSize,
            borderRadius: 12,
            justifyContent: "center" as const,
            alignItems: "center" as const,
          },
          isActive
            ? { backgroundColor: "#3B82F6" }
            : { backgroundColor: isDark ? "#1F2937" : "#F3F4F6" },
        ]}
      >
        <View style={{ position: "relative" }}>
          <Ionicons
            name={icon}
            size={24}
            color={isActive ? "#FFFFFF" : isDark ? "#F9FAFB" : "#374151"}
          />
          {badge !== undefined && badge > 0 && (
            <View
              style={{
                position: "absolute",
                top: -6,
                right: -10,
                backgroundColor: "#10B981",
                borderRadius: 10,
                minWidth: 18,
                height: 18,
                justifyContent: "center",
                alignItems: "center",
                paddingHorizontal: 4,
              }}
            >
              <Text
                style={{
                  fontSize: 11,
                  fontWeight: "700",
                  color: "#FFFFFF",
                }}
              >
                {badge}
              </Text>
            </View>
          )}
        </View>
        <Text
          style={{
            fontSize: 10,
            marginTop: 2,
            color: isActive ? "#FFFFFF" : isDark ? "#9CA3AF" : "#6B7280",
          }}
        >
          {label}
        </Text>
      </Pressable>
    );

    return (
      <View
        style={{
          alignItems: "center",
          paddingVertical: 16,
        }}
      >
        {/* Number buttons - 2 rows */}
        <View style={{ marginBottom: gap }}>
          {/* Row 1: 1-5 */}
          <View
            style={{
              flexDirection: "row",
              gap,
              marginBottom: gap,
            }}
          >
            {[1, 2, 3, 4, 5].map((num) => (
              <NumberButton
                key={num}
                num={num}
                isCompleted={completedNumbers.includes(num)}
              />
            ))}
          </View>

          {/* Row 2: 6-9 + controls */}
          <View
            style={{
              flexDirection: "row",
              gap,
            }}
          >
            {[6, 7, 8, 9].map((num) => (
              <NumberButton
                key={num}
                num={num}
                isCompleted={completedNumbers.includes(num)}
              />
            ))}
            {/* Erase button */}
            <ControlButton
              icon="backspace-outline"
              label="Wis"
              onPress={onClearPress}
            />
          </View>
        </View>

        {/* Control buttons row */}
        <View
          style={{
            flexDirection: "row",
            gap,
            marginTop: gap,
          }}
        >
          <ControlButton
            icon="arrow-undo-outline"
            label="Ongedaan"
            onPress={onUndoPress}
          />
          <ControlButton
            icon="pencil-outline"
            label="Notities"
            onPress={onNotesToggle}
            isActive={isNotesMode}
          />
          <ControlButton
            icon="bulb-outline"
            label="Hint"
            onPress={onHintPress}
            badge={hintsRemaining}
          />
        </View>

        {/* Notes mode explainer */}
        {isNotesMode && (
          <View
            style={{
              marginTop: 10,
              paddingHorizontal: 16,
              paddingVertical: 8,
              backgroundColor: isDark ? "#1E3A5F" : "#EFF6FF",
              borderRadius: 8,
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Ionicons
              name="information-circle-outline"
              size={16}
              color={isDark ? "#93C5FD" : "#3B82F6"}
            />
            <Text
              style={{
                fontSize: 12,
                color: isDark ? "#93C5FD" : "#3B82F6",
              }}
            >
              Selecteer een cel en tik een nummer om een notitie te plaatsen
            </Text>
          </View>
        )}
      </View>
    );
  }
);

NumberPad.displayName = "NumberPad";

